import { PageSetting } from '../contexts/PagesContext';
import { InputLimits } from '../enums/InputLimits';

interface ValidationRule {
  name?: string;
  min?: number;
  limit?: number;
  pattern?: RegExp;
  error?: string;
}

export const sanitizePageSettingsForm = (form: PageSetting): string[] => {
  const errors: string[] = [];
  const colorRegex = /^#[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/; // Matches hex colors with 3 or 6 digits

  const numericFields: Record<string, ValidationRule> = {
    smart_phone_width: {
      name: 'アートボード(1400px～)',
    },
    desk_top_width: {
      name: 'アートボード(1920px～)',
    },
  };

  const rangeFields: Record<string, ValidationRule> = {
    password: {
      min: InputLimits.PASSWORD_LIMIT_MIN,
      limit: InputLimits.PASSWORD_LIMIT_MAX,
      error: `パスワードは${InputLimits.PASSWORD_LIMIT_MIN}文字以上${InputLimits.PASSWORD_LIMIT_MAX}文字以下で入力してください。`,
    },
  };

  const textFields: Record<string, ValidationRule> = {
    // basic
    title: {
      limit: InputLimits.TITLE_LIMIT,
      error: `ページタイトル(title)は${InputLimits.TITLE_LIMIT}文字以内で入力してください。`,
    },
    description: {
      limit: InputLimits.DESCRIPTION_LIMIT,
      error: `ページの説明文(description)は${InputLimits.DESCRIPTION_LIMIT}文字以内で入力してください。`,
    },
    keywords: {
      limit: InputLimits.KEYWORDS_LIMIT,
      error: `ページのキーワード(keyword)は${InputLimits.KEYWORDS_LIMIT}文字以内で入力してください。`,
    },
    // scripts
    header_script: {
      limit: InputLimits.SEO_HEADER_SCRIPT,
      error: `headerタグ直下の埋め込みは${InputLimits.SEO_HEADER_SCRIPT}文字以内で入力してください。`,
    },
    body_script: {
      limit: InputLimits.SEO_BODY_SCRIPT,
      error: `bodyタグ直下の埋め込みは${InputLimits.SEO_BODY_SCRIPT}文字以内で入力してください。`,
    },
    google_analytics_script: {
      limit: InputLimits.GA4_SCRIPT,
      error: `GA4用のトラッキングコード は${InputLimits.GA4_SCRIPT}文字以内で入力してください。`,
    },
    // color settings
    main_color: {
      pattern: colorRegex,
      error: 'カラー設定のメイン色の形式が不正です。',
    },
    text_color: {
      pattern: colorRegex,
      error: 'カラー設定の文字色の形式が不正です。',
    },
    background_color: {
      pattern: colorRegex,
      error: 'カラー設定の背景色の形式が不正です。',
    },
    // auth settings
    auth_page_description: {
      limit: InputLimits.AUTH_PAGE_DESCRIPTION,
      error: `パスワード保護の表示テキストは${InputLimits.AUTH_PAGE_DESCRIPTION}文字以内で入力してください。`,
    },
  };

  Object.entries(textFields).forEach(([key, rule]) => {
    const value = form[key as keyof PageSetting];
    // Count string length matching PHP's strlen() behavior
    // When textarea content is sent to server, browsers convert \n to \r\n
    // So we need to count each \n as 2 characters to match PHP
    const stringValue = value ? value.toString() : '';
    const lineBreaks = (stringValue.match(/\n/g) || []).length;
    const actualLength = stringValue.length + lineBreaks;

    if (
      rule.limit !== undefined
      && typeof value === 'string'
      && actualLength > rule.limit
    ) {
      errors.push(rule.error || `${key}の入力が長すぎます。`);
    }

    if (
      rule.pattern
      && typeof value === 'string'
      && !rule.pattern.test(value)
    ) {
      errors.push(rule.error || `${key}の入力形式が不正です。`);
    }
  });

  Object.entries(numericFields).forEach(([key, rule]) => {
    const value = form[key as keyof PageSetting];

    if (typeof value === 'number') {
      if (rule.name && (value < 0 || value > InputLimits.NUMBER_LIMIT)) {
        errors.push(`${rule.name}は1以上${InputLimits.NUMBER_LIMIT}以下で入力してください。`);
      }
    }
  });

  Object.entries(rangeFields).forEach(([key, rule]) => {
    const value = form[key as keyof PageSetting];

    if (typeof value === 'string') {
      // Count string length matching PHP's strlen() behavior
      const lineBreaks = (value.match(/\n/g) || []).length;
      const length = value.length + lineBreaks;

      if (length === 0) return; // Skip empty strings when password is not required

      if (rule.min !== undefined && length < rule.min) {
        errors.push(rule.error || `${key}は${rule.min}文字以上で入力してください。`);
      }

      if (rule.limit !== undefined && length > rule.limit) {
        errors.push(rule.error || `${key}は${rule.limit}文字以下で入力してください。`);
      }
    }
  });

  return errors;
};
